import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Textarea,
} from "@fluentui/react-components";
import { useState } from "react";

import { errorReportCreate } from "../../services/api.service.js";

interface NavigatorWithMemory extends Navigator {
  deviceMemory?: number;
}

interface ErrorReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  error: Error;
}

export const ErrorReportDialog = ({
  isOpen,
  onClose,
  onSuccess,
  error,
}: ErrorReportDialogProps) => {
  const [userNote, setUserNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useStableCallback(async () => {
    setIsSubmitting(true);
    try {
      // Collect browser information
      const browserInfo: Record<string, unknown> = {
        cookieEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack,
        hardwareConcurrency: navigator.hardwareConcurrency,
        language: navigator.language,
        languages: navigator.languages,
        platform: navigator.platform,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        userAgent: navigator.userAgent,
        vendor: navigator.vendor,
      };

      // Collect device memory if available
      const navigatorWithMemory = navigator as NavigatorWithMemory;
      if ("deviceMemory" in navigatorWithMemory) {
        browserInfo.deviceMemory = navigatorWithMemory.deviceMemory;
      }

      // Collect max touch points if available
      if ("maxTouchPoints" in navigator) {
        browserInfo.maxTouchPoints = navigator.maxTouchPoints;
      }

      const payload = {
        browserInfo,
        error: {
          message: error.message || error.toString() || "Unknown issue",
          name: error.name || "Unknown",
          stack: error.stack || "No stack trace available",
        },
        pageContext: {
          href: window.location.href,
          referrer: document.referrer,
        },
        userNote: userNote.trim() || null,
      };

      await errorReportCreate(payload);
      onSuccess();
      onClose();
      setUserNote("");
    } catch {
      // If reporting fails, still allow the user to continue (silently)
    } finally {
      setIsSubmitting(false);
    }
  });

  const handleCancel = useStableCallback(() => {
    onClose();
    setUserNote("");
  });
  const handleChange125 = useStableCallback((_, data) =>
    setUserNote(data.value)
  );

  const handleOpenChange93 = useStableCallback(
    (_, data) => !data.open && onClose()
  );

  return (
    <Dialog onOpenChange={handleOpenChange93} open={isOpen}>
      <DialogSurface className="sm:max-h-[90lvh]">
        <DialogBody className="sm:max-h-[90lvh]">
          <DialogTitle>Report an Issue</DialogTitle>
          <DialogContent className="overflow-y-auto">
            <div className="mb-4 space-y-3">
              <p className="text-[14px] text-gray-700">
                Help us improve the application by sharing details about this
                issue.
              </p>

              <div className="rounded bg-gray-50 p-3">
                <p className="mb-2 text-[12px] font-semibold text-gray-800">
                  What data is collected:
                </p>
                <ul className="ml-4 list-disc space-y-1 text-[12px] text-gray-600">
                  <li>Technical details (message and stack trace)</li>
                  <li>Page URL and browser information</li>
                  <li>Your account details (if signed in)</li>
                  <li>Optional: Your description</li>
                </ul>
              </div>

              <p className="text-[12px] text-gray-600">
                This information helps our team diagnose and fix issues faster.
                We value your feedback and use it solely to improve the
                application.
              </p>

              <Textarea
                className="w-full"
                disabled={isSubmitting}
                onChange={handleChange125}
                placeholder="What were you doing when this issue occurred? (optional)"
                rows={4}
                value={userNote}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="transparent"
              className="text-[14px] text-[#0078d4] underline hover:no-underline"
              disabled={isSubmitting}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={isSubmitting}
              onClick={handleSubmit}
            >
              {isSubmitting ? "Sending..." : "Send Report"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
